import {
  Camera,
  FolderKanban,
  Users,
  Mail,
  History,
  ChartColumn,
  BookOpen,
  CreditCard,
  LayoutDashboard,
  Settings,
  type LucideIcon,
} from 'lucide-react'
import { ROUTES } from '@/config/routes'

// The Anleitung manual is static HTML outside the SPA route tree, so it must
// be reached with a full document load. Use the explicit file path: on native,
// Capacitor's local server serves the bundled asset directly, whereas a bare
// `/anleitung` has no exact file match and falls through to the SPA index.html
// (which both fails to show the manual AND respawns PowerSync — see the
// "database is locked (code 5)" gotcha in CLAUDE.md).
export const MANUAL_HREF = '/anleitung/index.html'

export interface NavItem {
  href: string
  icon: LucideIcon
  labelKey:
    | 'detect'
    | 'projects'
    | 'contractors'
    | 'tickets'
    | 'history'
    | 'dashboard'
    | 'manual'
    | 'billing'
    | 'admin'
    | 'systemSettings'
  /** Static HTML outside the route tree — must full-load via plain <a> (+ openManual on native). */
  external?: boolean
}

export interface NavSections {
  /** Fixed order mandated by client feedback (2026-06): Foto / KI Analyse,
   *  Projekte, Auftragnehmer, Mangelanzeige, Historie, Statistiken,
   *  Tipps & Hilfe. */
  main: NavItem[]
  /** Admin tools — rendered below a divider at the very bottom. */
  admin: NavItem[]
}

export function getNavSections(userRole: string | null | undefined): NavSections {
  const siteManagerPlus =
    userRole === 'SITE_MANAGER' || userRole === 'ADMIN' || userRole === 'SUPERUSER'
  const adminPlus = userRole === 'ADMIN' || userRole === 'SUPERUSER'

  const main: NavItem[] = [
    { href: ROUTES.DETECT, icon: Camera, labelKey: 'detect' },
    { href: ROUTES.PROJECTS, icon: FolderKanban, labelKey: 'projects' },
  ]
  if (siteManagerPlus) {
    main.push(
      { href: ROUTES.CONTRACTORS, icon: Users, labelKey: 'contractors' },
      { href: ROUTES.TICKETS, icon: Mail, labelKey: 'tickets' },
    )
  }
  main.push(
    { href: ROUTES.HISTORY, icon: History, labelKey: 'history' },
    { href: ROUTES.DASHBOARD, icon: ChartColumn, labelKey: 'dashboard' },
    { href: MANUAL_HREF, icon: BookOpen, labelKey: 'manual', external: true },
  )

  const admin: NavItem[] = []
  if (adminPlus) {
    admin.push(
      { href: ROUTES.BILLING, icon: CreditCard, labelKey: 'billing' },
      { href: ROUTES.ADMIN, icon: LayoutDashboard, labelKey: 'admin' },
    )
  }
  if (userRole === 'SUPERUSER') {
    admin.push({ href: ROUTES.SYSTEM_SETTINGS, icon: Settings, labelKey: 'systemSettings' })
  }

  return { main, admin }
}
